// =============================================================
// GachaOverlay — daily gacha roll that opens from the status page.
// State machine: idle -> rolling -> result -> close.
// History persists in localStorage.
// =============================================================

import { useEffect, useState } from 'react'
import {
  GACHA_EMOJI,
  GACHA_HISTORY_KEY,
  GACHA_HISTORY_LIMIT,
  GACHA_POOL,
  gachaTodayKey,
  gachaUsedToday,
  type GachaHistoryEntry,
  type GachaRarity,
} from '../lib/yuzu-data'

interface GachaOverlayProps {
  open: boolean
  onClose: () => void
}

interface PendingResult {
  rarity: GachaRarity
  label: string
  prize: string
  desc: string
}

const RARITY_KEYS: GachaRarity[] = ['ssr', 'sr', 'r', 'n']

export function GachaOverlay({ open, onClose }: GachaOverlayProps) {
  const [rolling, setRolling] = useState(false)
  const [result, setResult] = useState<PendingResult | null>(null)
  const [used, setUsed] = useState(false)
  const [history, setHistory] = useState<GachaHistoryEntry[]>([])
  const [ssrBurst, setSsrBurst] = useState(false)
  const [capsuleEmoji, setCapsuleEmoji] = useState('🥚')

  // Re-read state whenever the overlay opens
  useEffect(() => {
    if (!open) return
    setResult(null)
    setUsed(gachaUsedToday())
    setCapsuleEmoji('🥚')
    try {
      const raw = localStorage.getItem(GACHA_HISTORY_KEY) || '[]'
      setHistory(JSON.parse(raw) as GachaHistoryEntry[])
    } catch {
      setHistory([])
    }
  }, [open])

  function pickRarity(): GachaRarity {
    const roll = Math.random() * 100
    let cumul = 0
    for (const key of RARITY_KEYS) {
      cumul += GACHA_POOL[key].weight
      if (roll < cumul) return key
    }
    return 'n'
  }

  function doRoll() {
    if (rolling || used) return
    setRolling(true)
    setResult(null)
    setTimeout(() => {
      const rarity = pickRarity()
      const pool = GACHA_POOL[rarity]
      const item = pool.items[Math.floor(Math.random() * pool.items.length)]
      const final: PendingResult = {
        rarity,
        label: pool.label,
        prize: item.prize,
        desc: item.desc,
      }
      setResult(final)
      setCapsuleEmoji(GACHA_EMOJI[rarity])
      setRolling(false)
      setUsed(true)

      if (rarity === 'ssr') {
        setSsrBurst(true)
        window.setTimeout(() => setSsrBurst(false), 1200)
      }

      // Persist
      localStorage.setItem(gachaTodayKey(), '1')
      const entry: GachaHistoryEntry = {
        date: new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10),
        rarity,
        label: pool.label,
        prize: item.prize,
      }
      const next = [entry, ...history].slice(0, GACHA_HISTORY_LIMIT)
      setHistory(next)
      localStorage.setItem(GACHA_HISTORY_KEY, JSON.stringify(next))
    }, 800)
  }

  return (
    <>
      {ssrBurst && <div className="gacha-ssr-burst" />}
      <div
        className={`gacha-overlay ${open ? 'open' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div className="gacha-box">
          <div className="gacha-title">🎰 琥珀扭蛋机</div>
          <div
            className={`gacha-capsule ${rolling ? 'shaking' : ''}`}
            onClick={doRoll}
            style={{
              opacity: used && !result ? 0.5 : 1,
              pointerEvents: rolling || (used && !result) ? 'none' : 'auto',
            }}
          >
            {capsuleEmoji}
          </div>
          <div style={{ fontSize: 13, color: 'var(--cloud-dark)' }}>
            {rolling
              ? '扭蛋中...'
              : used
              ? '今天已经扭过啦～'
              : '点蛋扭一次！'}
          </div>
          {result && (
            <div className="gacha-result show">
              <div className={`gacha-rarity ${result.rarity}`}>{result.label}</div>
              <div className="gacha-prize">{result.prize}</div>
              <div className="gacha-desc">{result.desc}</div>
            </div>
          )}
          {result && (
            <button className="gacha-btn" onClick={onClose}>
              收下 🧡
            </button>
          )}
          <div className="gacha-hint">
            {used && !result ? '明天再来喵 🐱' : '每天一次 · 点🧡打开'}
          </div>
          {history.length > 0 && (
            <div className="gacha-history">
              <div className="gacha-history-title">📋 扭蛋记录</div>
              {history.map((h, i) => (
                <div key={i} className="gacha-history-item">
                  <span className="gh-date">{(h.date || '').slice(5)}</span>
                  <span className={`gh-rarity ${h.rarity}`}>{h.label || 'N'}</span>
                  <span>{h.prize}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}